// Colour maths for ColorPicker.vue and anything else that works with hex colours. Hex colours are uppercase #RRGGBB.

// Hue in degrees [0, 360); saturation and value in [0, 1].
export interface ColorHsv {
  h: number;
  s: number;
  v: number;
}

export function colorRgb(hex: string): [number, number, number] {
  const n = parseInt(hex.slice(1), 16);
  return [n >> 16, (n >> 8) & 255, n & 255];
}

export function colorFromRgb(rgb: [number, number, number]): string {
  return (
    '#' +
    rgb
      .map((c) => Math.round(Math.min(255, Math.max(0, c))))
      .map((c) => c.toString(16).padStart(2, '0'))
      .join('')
      .toUpperCase()
  );
}

// Moves `amount` of the way from `hex` toward `toward`.
export function colorMix(hex: string, toward: string, amount: number): string {
  const to = colorRgb(toward);
  const from = colorRgb(hex);
  return colorFromRgb([0, 1, 2].map((i) => from[i] + (to[i] - from[i]) * amount) as [number, number, number]);
}

// Sum of the per-channel differences.
export function colorDistance(a: string, b: string): number {
  const bc = colorRgb(b);
  return colorRgb(a).reduce((sum, c, i) => sum + Math.abs(c - bc[i]), 0);
}

// What someone typed, as a hex colour: with or without the #, in three or six digits. Null if it isn't one.
export function colorHexParse(text: string): string | null {
  const match = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(text.trim());
  if (!match) return null;
  const digits = match[1].length === 3 ? [...match[1]].map((d) => d + d).join('') : match[1];
  return `#${digits.toUpperCase()}`;
}

export function colorHexToHsv(hex: string): ColorHsv {
  const [r, g, b] = colorRgb(hex).map((c) => c / 255);
  const max = Math.max(r, g, b);
  const d = max - Math.min(r, g, b);
  let h = 0;
  if (d > 0) {
    if (max === r) h = ((g - b) / d) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
  }
  h *= 60;
  return { h: h < 0 ? h + 360 : h, s: max === 0 ? 0 : d / max, v: max };
}

export function colorHsvToHex({ h, s, v }: ColorHsv): string {
  const channel = (n: number) => {
    const k = (n + h / 60) % 6;
    return (v - v * s * Math.max(0, Math.min(k, 4 - k, 1))) * 255;
  };
  return colorFromRgb([channel(5), channel(3), channel(1)]);
}
